import type { FormEvent, ReactNode } from 'react';
import { useState } from 'react';
import { useAccountContext } from '@gonfalon/app-context/useAccountContext';
import { useTypedAppForm } from '@gonfalon/forms/hooks';
import { trackContactSalesLinkClicked } from '@gonfalon/marketo/analytics';
import { Message } from '@gonfalon/messages/Message';
import { msg } from '@gonfalon/messages/msg';
import { NewTabLink } from '@gonfalon/ui/NewTabLink';
import {
  Button,
  Dialog,
  DialogTrigger,
  Form,
  Heading,
  IconButton,
  Modal,
  ModalOverlay,
} from '@launchpad-ui/components';
import cx from 'clsx';
import { z } from 'zod';

import type { ContactUsFormValues, ContactUsProps } from './types';

import styles from './ContactUs.module.css';

const FormSchema = z.object({
  email: z.email({ error: () => msg('billing.contactSales.emailInvalid') }),
  company: z.string().min(1, { error: () => msg('billing.contactSales.companyRequired') }),
  firstName: z.string().min(1, { error: () => msg('billing.contactSales.firstNameRequired') }),
  lastName: z.string().min(1, { error: () => msg('billing.contactSales.lastNameRequired') }),
  jobTitle: z.string().optional(),
  moreDetails: z.string().optional(),
});

type FormData = z.infer<typeof FormSchema>;

type ContactUsDialogProps = ContactUsProps & {
  isPending?: boolean;
  /** Invisible reCAPTCHA widget, if the submit path needs one. */
  captcha?: ReactNode;
  /** Called with the form values and a callback that closes the dialog. */
  onSubmit: (values: ContactUsFormValues, close: () => void) => void;
};

/**
 * The contact sales trigger button and its dialog. Owns the fields, their
 * validation, and open/close; the caller owns where the submission goes.
 */
export function ContactUsDialog({
  plan,
  size = 'large',
  variant = 'default',
  cta,
  defaults,
  source,
  hideTrigger = false,
  isOpen: isOpenProp,
  onOpenChange: onOpenChangeProp,
  isPending = false,
  captcha,
  onSubmit,
}: ContactUsDialogProps) {
  const { profile, account } = useAccountContext();
  const [internalIsOpen, setInternalIsOpen] = useState(false);
  const isControlled = isOpenProp !== undefined;
  const isOpen = isControlled ? isOpenProp : internalIsOpen;

  const setIsOpen = (open: boolean) => {
    if (isControlled) {
      onOpenChangeProp(open);
    } else {
      setInternalIsOpen(open);
    }
  };

  const onCloseModal = () => {
    form.reset();
    setIsOpen(false);
  };

  const form = useTypedAppForm<FormData>({
    defaultValues: {
      email: profile?.email || '',
      company: account?.organization || '',
      firstName: profile?.firstName || '',
      lastName: profile?.lastName || '',
      jobTitle: '',
      moreDetails: defaults?.moreDetails || '',
    },
    validators: {
      onChange: FormSchema,
      onSubmit: FormSchema,
      onMount: FormSchema,
    },
    onSubmit: ({ value }) => onSubmit(value, onCloseModal),
  });

  const { AppForm, AppField, Subscribe } = form;

  const buttonCTA = () => cta ?? msg('billing.contactSales.contactUs');

  const handleFormSubmit = (e: FormEvent) => {
    e.preventDefault();
    void form.handleSubmit();
  };

  function handleOpenChange(open: boolean) {
    setIsOpen(open);
    if (!open) {
      form.reset();
    }
  }

  const handleTriggerPress = () => {
    trackContactSalesLinkClicked({ planType: plan?.planType, component: source, cta });
    setIsOpen(true);
  };

  const modal = (
    <ModalOverlay isOpen={hideTrigger ? isOpen : undefined} onOpenChange={hideTrigger ? handleOpenChange : undefined}>
      <Modal>
        <Dialog aria-label={msg('billing.contactSales.dialogLabel')}>
          {() => (
            <Form className={styles.form} onSubmit={handleFormSubmit}>
              <AppForm>
                <div slot="header">
                  <Heading slot="title">
                    <Message id="billing.contactSales.heading" />
                  </Heading>
                  <IconButton
                    aria-label={msg('billing.contactSales.closeLabel')}
                    icon="cancel"
                    size="small"
                    variant="minimal"
                    onPress={onCloseModal}
                  />
                </div>
                <div className={styles.formBody} slot="body">
                  <AppField name="email">
                    {(field) => (
                      <field.TextInputField
                        label={msg('billing.contactSales.emailLabel')}
                        type="email"
                        placeholder={msg('billing.contactSales.emailPlaceholder')}
                        isRequired
                      />
                    )}
                  </AppField>
                  <AppField name="company">
                    {(field) => <field.TextInputField label={msg('billing.contactSales.companyLabel')} isRequired />}
                  </AppField>
                  <AppField name="firstName">
                    {(field) => <field.TextInputField label={msg('billing.contactSales.firstNameLabel')} isRequired />}
                  </AppField>
                  <AppField name="lastName">
                    {(field) => <field.TextInputField label={msg('billing.contactSales.lastNameLabel')} isRequired />}
                  </AppField>
                  <AppField name="jobTitle">
                    {(field) => <field.TextInputField label={msg('billing.contactSales.jobTitleLabel')} />}
                  </AppField>
                  <div className={styles.wide}>
                    <AppField name="moreDetails">
                      {(field) => (
                        <field.TextAreaInputField
                          label={msg('billing.contactSales.moreDetailsLabel')}
                          className={styles.moreDetailsTextArea}
                        />
                      )}
                    </AppField>
                  </div>
                  <div className={cx(styles.wide, styles.disclaimer)}>
                    <Message
                      id="billing.contactSales.disclaimer"
                      values={{
                        privacyLink: (chunks) => (
                          <NewTabLink
                            href="https://launchdarkly.com/policies/privacy/"
                            text={chunks.join('')}
                            showIcon={false}
                          />
                        ),
                        optOutLink: (chunks) => (
                          <NewTabLink
                            href="https://go.launchdarkly.com/UnsubscribePage.html"
                            text={chunks.join('')}
                            showIcon={false}
                          />
                        ),
                      }}
                    />
                  </div>
                  {captcha}
                </div>
                <div slot="footer">
                  <Button onPress={onCloseModal}>{msg('billing.contactSales.cancel')}</Button>
                  <Subscribe selector={(state) => state.isValid}>
                    {(isValid) => (
                      <Button
                        variant="primary"
                        type="submit"
                        data-test-id="contact-us-submit-button"
                        aria-label={msg('billing.contactSales.submitLabel')}
                        isDisabled={!isValid || isPending}
                        isPending={isPending}
                      >
                        {msg('billing.contactSales.submit')}
                      </Button>
                    )}
                  </Subscribe>
                </div>
              </AppForm>
            </Form>
          )}
        </Dialog>
      </Modal>
    </ModalOverlay>
  );

  if (hideTrigger) {
    return modal;
  }

  return (
    <DialogTrigger isOpen={isOpen} onOpenChange={handleOpenChange}>
      <Button
        data-test-id="contact-us-button"
        variant={variant}
        className={cx(styles.contactUsButton, variant === 'minimal' && styles.minimal)}
        size={size}
        onPress={handleTriggerPress}
      >
        {buttonCTA()}
      </Button>
      {modal}
    </DialogTrigger>
  );
}
